import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import GeoContext from 'contexts/geoContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import type { EnrichmentStatus, ItineraryUpdate, EnrichmentCompleteEvent, TripItineraryData } from 'tripPlanner/api/types'
import { applyItineraryUpdates, applyEnrichmentUpdate } from 'tripPlanner/utils/itineraryUpdates'
import { patchExperienceUpdatesWithPendingDaySelection } from 'tripPlanner/utils/pendingExperienceDaySelection'
import { useGetItinerary } from 'tripPlanner/hooks/api/chat'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { computeTripDatesFromStops, formatStopDateRange, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { buildFlightName } from '../utils/deleteTripMessages'

interface UseItineraryUpdatesArgs {
  chatId: number | undefined
  // Ref to track streaming state - prevents refetchOnWindowFocus from overwriting optimistic updates mid-stream
  isStreamingRef: React.RefObject<boolean>
}

export interface ItineraryScrollTarget {
  stopId: number
  dayId?: number
  itemId?: number
}

interface UseItineraryUpdatesReturn {
  itineraryData: TripItineraryData | undefined
  enrichingStopIds: Set<number>
  enrichingItemIds: Set<number>
  scrollTarget: ItineraryScrollTarget | undefined
  clearScrollTarget: () => void
  // Streaming callbacks receive chatId as first argument to avoid stale closure issues
  handleItineraryUpdates: (chatIdArg: number, updates: Array<ItineraryUpdate>) => void
  handleEnrichmentComplete: (chatIdArg: number, event: EnrichmentCompleteEvent) => void
  handleRefetchItinerary: (chatIdArg: number) => void
  // Non-streaming callbacks use closure chatId (safe for user-initiated actions on current chat)
  refetchCurrentChatItinerary: () => void
}

// ── Deletion snackbar name lookup ─────────────────────────────────────
// After applyItineraryUpdates strips the entity from the cache the snackbar
// still needs to name it. These helpers read from the prior snapshot captured
// before the reducer ran.

function findDeletedStopName(prev: TripItineraryData | undefined, stopId: number): string | undefined {
  return prev?.stops.find(s => s.id === stopId)?.placeName
}

function findDeletedDayLabel(
  prev: TripItineraryData | undefined,
  dayId: number,
  intl: IntlShape,
): string | undefined {
  for (const stop of prev?.stops ?? []) {
    const day = stop.days.find(d => d.id === dayId)
    if (day) return getLocalisedDayLabel(intl, day.dayNumber, day.date)
  }
  return undefined
}

function findDeletedItemName(prev: TripItineraryData | undefined, itemId: number): string | undefined {
  for (const stop of prev?.stops ?? []) {
    for (const day of stop.days) {
      const item = day.items.find(i => i.id === itemId)
      if (item) return item.name
    }
    const item = (stop.itemsWithoutDay ?? []).find(i => i.id === itemId)
    if (item) return item.name
    for (const transport of stop.transports ?? []) {
      const option = transport.options.find(o => o.itemId === itemId)
      if (option) return buildFlightName(option, transport.name)
    }
  }
  return undefined
}

// ── Enrichment derivation helpers ─────────────────────────────────────

const ENRICHING_STATUSES: ReadonlySet<EnrichmentStatus> = new Set(['pending', 'enriching'])

function isEnriching(entity: { enrichmentStatus?: EnrichmentStatus }): boolean {
  return entity.enrichmentStatus !== undefined && ENRICHING_STATUSES.has(entity.enrichmentStatus)
}

function collectEnrichingIds(
  entities: ReadonlyArray<{ id: number; enrichmentStatus?: EnrichmentStatus }>,
  ids: Set<number>,
): void {
  for (const entity of entities) {
    if (isEnriching(entity)) ids.add(entity.id)
  }
}

/**
 * Returns a referentially-stable Set that only changes identity when its contents change.
 * Prevents unnecessary re-renders of ItineraryContext consumers and refetchInterval churn
 * when itineraryData reference changes but the derived enriching IDs remain the same.
 */
function useStableSet(rawSet: Set<number>): Set<number> {
  const ref = useRef(rawSet)

  if (!setsAreEqual(rawSet, ref.current)) {
    ref.current = rawSet
  }

  return ref.current
}

function setsAreEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) {
    if (!b.has(id)) return false
  }
  return true
}

// ── Hook ──────────────────────────────────────────────────────────────

/**
 * Manages itinerary state for the chat trip planner.
 *
 * Data Flow:
 * 1. React Query (`useGetItinerary`) is the single source of truth for itinerary data
 * 2. During streaming, optimistic updates are applied via `queryClient.setQueryData`
 * 3. After stream completes, `handleRefetchItinerary` invalidates the cache to sync with server
 * 4. `refetchOnWindowFocus` is disabled during streaming to prevent overwriting optimistic updates
 *    - When not streaming, window focus triggers refetch to ensure fresh data
 *
 * Enrichment State (derived, not managed):
 * - `enrichingStopIds` / `enrichingItemIds` are derived from `enrichmentStatus` on itinerary entities
 * - Self-terminating: enrichment finishes → status becomes terminal → Sets empty → refetchInterval stops
 * - Self-resetting: chat switch → itinerary data changes → Sets reflect new chat automatically
 * - `refetchInterval` (3s) acts as a fallback when the stream closes before all enrichment completes
 *   (e.g., 15s BE timeout exceeded). Stream events handle the happy path via optimistic cache patches.
 */
export function useItineraryUpdates({
  chatId,
  isStreamingRef,
}: UseItineraryUpdatesArgs): UseItineraryUpdatesReturn {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const { currentRegionCode } = useContext(GeoContext)

  // refetchInterval is driven by derived enrichment state. useState + sync-during-render
  // ensures the interval is current on the same render cycle that detects enriching entities,
  // avoiding the one-render lag that a ref-based approach would have.
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)

  const { data: itineraryData } = useGetItinerary(chatId, currentRegionCode, {
    refetchOnWindowFocus: () => !isStreamingRef.current,
    refetchInterval,
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false
      return error !== null && typeof error === 'object' && 'status' in error && error.status === 403
    },
    retryDelay: 1000,
  })

  // ── Derive enriching entity Sets from itinerary data ──
  // Single pass: collects both stop and item IDs in one iteration.
  // Only 'pending' and 'enriching' are active statuses. undefined = doesn't need enrichment.

  const { stopIds: enrichingStopIdsRaw, itemIds: enrichingItemIdsRaw } = useMemo(() => {
    const stopIds = new Set<number>()
    const itemIds = new Set<number>()
    for (const stop of itineraryData?.stops ?? []) {
      if (isEnriching(stop)) stopIds.add(stop.id)
      for (const day of stop.days) {
        collectEnrichingIds(day.items, itemIds)
      }
      collectEnrichingIds(stop.itemsWithoutDay ?? [], itemIds)
    }
    return { stopIds, itemIds }
  }, [itineraryData])

  const enrichingStopIds = useStableSet(enrichingStopIdsRaw)
  const enrichingItemIds = useStableSet(enrichingItemIdsRaw)

  // Sync refetchInterval with derived enrichment state (setState-during-render pattern).
  // React immediately re-renders before commit, so useGetItinerary gets the correct interval.
  const nextInterval: number | false = (enrichingStopIds.size > 0 || enrichingItemIds.size > 0) ? 3000 : false
  if (nextInterval !== refetchInterval) {
    setRefetchInterval(nextInterval)
  }

  const [scrollTarget, setScrollTarget] = useState<ItineraryScrollTarget | undefined>(undefined)

  const clearScrollTarget = useCallback(() => {
    setScrollTarget(undefined)
  }, [])

  // chatIdArg is passed from streaming code to avoid stale closure (streaming has authoritative chatId)
  const handleItineraryUpdates = useCallback((chatIdArg: number, updates: Array<ItineraryUpdate>) => {
    if (updates.length === 0) return

    const prevData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
    )
    const prevDates = computeTripDatesFromStops(prevData?.stops)
    const normalizedUpdates = patchExperienceUpdatesWithPendingDaySelection(prevData, updates)

    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
        (prev) => applyItineraryUpdates(prev, normalizedUpdates),
      )
    } catch {
      // Optimistic update failed, will be corrected by server response
    }

    const newData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
    )
    const newDates = computeTripDatesFromStops(newData?.stops)

    const tripDatesChanged = prevDates?.startDate !== newDates?.startDate || prevDates?.endDate !== newDates?.endDate

    if (tripDatesChanged) {
      const dateMessage = newDates
        ? intl.formatMessage(
            {
              id: 'OOhdWO0_xvfM9OLPUY-K',
              defaultMessage: 'Trip dates updated to {dateRange}',
              description: 'Snackbar message when trip dates are set or changed',
            },
            { dateRange: formatStopDateRange(newDates.startDate, newDates.endDate) },
          )
        : intl.formatMessage({
            id: 'forhGndOznvrIFs49brO',
            defaultMessage: 'Trip dates removed',
            description: 'Snackbar message when trip dates are cleared in the trip planner',
          })
      showSnackbar(dateMessage, 'success', { icon: <LineCheckCircleIcon /> })
    } else {
      // Deletion snackbars are resolved first, prioritised stop > day > item.
      // A stop delete can cascade to item deletes in the same batch (transport
      // cleanup + orphan sweep in execDeleteStop) — we want a single "Deleted
      // Paris" snackbar for that cascade, not three overlapping toasts.
      const deletedStop = normalizedUpdates.find((u): u is Extract<ItineraryUpdate, { kind: 'stop_deleted' }> => u.kind === 'stop_deleted')
      const deletedDay = normalizedUpdates.find((u): u is Extract<ItineraryUpdate, { kind: 'day_deleted' }> => u.kind === 'day_deleted')
      const deletedItem = normalizedUpdates.find((u): u is Extract<ItineraryUpdate, { kind: 'item_deleted' }> => u.kind === 'item_deleted')

      let deletionMessage: string | undefined
      if (deletedStop) {
        const placeName = findDeletedStopName(prevData, deletedStop.stopId)
        deletionMessage = placeName
          ? intl.formatMessage(
              {
                id: 'b6T-QWZ36x_q_3ma_LWk',
                defaultMessage: 'Deleted {placeName}',
                description: 'Snackbar message when a stop is deleted from the itinerary',
              },
              { placeName },
            )
          : intl.formatMessage({
              id: '-aMtu0XvTLCNQFTnIlGq',
              defaultMessage: 'Stop deleted',
              description: 'Fallback snackbar message when a stop is deleted but its name cannot be resolved',
            })
      } else if (deletedDay) {
        const dayLabel = findDeletedDayLabel(prevData, deletedDay.dayId, intl)
        deletionMessage = dayLabel
          ? intl.formatMessage(
              {
                id: 'aanrT825yRTCsiNFAtz6',
                defaultMessage: 'Deleted {dayLabel}',
                description: 'Snackbar message when a day is deleted from the itinerary',
              },
              { dayLabel },
            )
          : intl.formatMessage({
              id: 'mZAV79IRE314ePFnwER9',
              defaultMessage: 'Day deleted',
              description: 'Fallback snackbar message when a day is deleted but its label cannot be resolved',
            })
      } else if (deletedItem) {
        const itemName = findDeletedItemName(prevData, deletedItem.itemId)
        deletionMessage = itemName
          ? intl.formatMessage(
              {
                id: 'Y2o2SfSlgCF-5VoViWBf',
                defaultMessage: 'Deleted {itemName}',
                description: 'Snackbar message when an item is deleted from the itinerary',
              },
              { itemName },
            )
          : intl.formatMessage({
              id: 'ne03zmmhvZyZDD5tMnuy',
              defaultMessage: 'Item deleted',
              description: 'Fallback snackbar message when an item is deleted but its name cannot be resolved',
            })
      }

      if (deletionMessage) {
        showSnackbar(deletionMessage, 'success', { icon: <LineCheckCircleIcon /> })
        // Early return is load-bearing: it also skips the scroll-target block
        // below, because we don't want to scroll-to-new on a delete.
        return
      }

      const addedStops = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_added' }> => u.kind === 'stop_added')
      const updatedStops = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_updated' }> => u.kind === 'stop_updated')
      const addedItems = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_added' }> => u.kind === 'item_added')
      const updatedItems = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_updated' }> => u.kind === 'item_updated')
      const messages: Array<string> = []
      if (addedStops.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: '1L5oieOS5nXMzEecmbmc',
            defaultMessage: 'Added {placeName}',
            description: 'Snackbar message when a single stop is added to the itinerary',
          },
          {
            placeName: addedStops[0].stop?.placeName ?? intl.formatMessage({
              id: 'yRxcIING_tY4NfEYDlnm',
              defaultMessage: 'stop',
              description: 'Fallback noun for an unnamed itinerary stop in the itinerary',
            }),
          },
        ))
      } else if (addedStops.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'gbT2QXUA1pHsHaGJKBv5',
            defaultMessage: 'Added {count, plural, one {# stop} other {# stops}}',
            description: 'Snackbar message when multiple stops are added to the itinerary',
          },
          { count: addedStops.length },
        ))
      }
      if (updatedStops.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: 'o0r6AGrK7nx0i237bl-T',
            defaultMessage: 'Updated {placeName}',
            description: 'Snackbar message when a single stop is updated in the itinerary',
          },
          {
            placeName: updatedStops[0].stop?.placeName ?? intl.formatMessage({
              id: 'yRxcIING_tY4NfEYDlnm',
              defaultMessage: 'stop',
              description: 'Fallback noun for an unnamed itinerary stop in the itinerary',
            }),
          },
        ))
      } else if (updatedStops.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'J5eoKi5xlhBeMup0zBFO',
            defaultMessage: 'Updated {count, plural, one {# stop} other {# stops}}',
            description: 'Snackbar message when multiple stops are updated in the itinerary',
          },
          { count: updatedStops.length },
        ))
      }
      if (addedItems.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: '2JwfVbdF2LkiebOOWC9U',
            defaultMessage: 'Added {activityName}',
            description: 'Snackbar message when a single activity is added to the itinerary',
          },
          {
            activityName: addedItems[0].item?.name ?? intl.formatMessage({
              id: 'HAWquKfUttGOwlqZwX8E',
              defaultMessage: 'activity',
              description: 'Fallback noun for an unnamed itinerary activity in the trip planner AI chat',
            }),
          },
        ))
      } else if (addedItems.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'Z9GKbmBRetJbmHeREI0w',
            defaultMessage: 'Added {count, plural, one {# activity} other {# activities}}',
            description: 'Snackbar message when multiple activities are added to the itinerary',
          },
          { count: addedItems.length },
        ))
      }
      if (updatedItems.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: 'dIm0WdYgXEXVtrICXXMJ',
            defaultMessage: 'Updated {activityName}',
            description: 'Snackbar message when a single activity is updated in the itinerary',
          },
          {
            activityName: updatedItems[0].item?.name ?? intl.formatMessage({
              id: 'HAWquKfUttGOwlqZwX8E',
              defaultMessage: 'activity',
              description: 'Fallback noun for an unnamed itinerary activity in the trip planner AI chat',
            }),
          },
        ))
      } else if (updatedItems.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'xGkpsy0xv_crKqLnoOR2',
            defaultMessage: 'Updated {count, plural, one {# activity} other {# activities}}',
            description: 'Snackbar message when multiple activities are updated in the itinerary',
          },
          { count: updatedItems.length },
        ))
      }

      if (messages.length > 0) {
        showSnackbar(messages[0], 'success', { icon: <LineCheckCircleIcon /> })
      }
    }

    const firstUpdate = normalizedUpdates[0]
    if (firstUpdate) {
      let scrollStopId: number | undefined
      let scrollDayId: number | undefined
      const scrollItemId = 'itemId' in firstUpdate ? firstUpdate.itemId : undefined

      if ('stopVisits' in firstUpdate && firstUpdate.stopVisits?.length > 0) {
        scrollStopId = firstUpdate.stopVisits[0].tripStopId
        scrollDayId = firstUpdate.stopVisits[0].startDayId
      } else if ('stopId' in firstUpdate) {
        scrollStopId = firstUpdate.stopId
        scrollDayId = 'dayId' in firstUpdate ? firstUpdate.dayId : undefined
      }

      if (scrollStopId !== undefined) {
        setScrollTarget({ stopId: scrollStopId, dayId: scrollDayId, itemId: scrollItemId })
      }
    }
  }, [queryClient, intl, currentRegionCode])

  const handleEnrichmentComplete = useCallback((chatIdArg: number, event: EnrichmentCompleteEvent) => {
    // Optimistic cache patch — sets enrichmentStatus: 'enriched' on the entity so the
    // derived enriching Sets immediately exclude it. Also merges enrichment data (images, place detail).
    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
        (prev) => applyEnrichmentUpdate(prev, event),
      )
    } catch {
      // Enrichment update failed, will be corrected by server response
    }
  }, [queryClient, currentRegionCode])

  const handleRefetchItinerary = useCallback((chatIdArg: number) => {
    queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatIdArg) })
  }, [queryClient])

  // Wrapper for non-streaming refetch (user actions on current chat)
  const refetchCurrentChatItinerary = useCallback(() => {
    if (chatId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatId) })
    }
  }, [chatId, queryClient])

  return {
    itineraryData,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    handleItineraryUpdates,
    handleEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  }
}
